import dayjs from 'dayjs';
import { borderClassFromBgClass } from '@/lib/badgeBorderFromBackground';
import { isPortage } from '@/constants/legalEntityType';
import { isMasterAgreementDocument } from '@/utils/masterAgreement';

export const MISSION_LIFECYCLE_STATUS = {
  DRAFT: 'DRAFT',
  AWAITING_FREELANCER_ACCEPTANCE: 'AWAITING_FREELANCER_ACCEPTANCE',
  AWAITING_TEAM_SIGNATURE: 'AWAITING_TEAM_SIGNATURE',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  TERMINATED: 'TERMINATED',
  DELETED: 'DELETED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
};

export function normalizeMissionLifecycleStatus(status) {
  return String(status ?? '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_')
}

export function isMissionPendingMemberApproval(mission) {
  const approvalStatus = String(mission?.approval_status ?? mission?.approvalStatus ?? '')
    .trim()
    .toLowerCase()
  return approvalStatus === 'pending'
}

function documentHasCollectedSignature(doc) {
  if (!doc || typeof doc !== 'object') return false
  return Boolean(
    doc.team_signed_at ||
      doc.teamSignedAt ||
      doc.freelancer_signed_at ||
      doc.freelancerSignedAt
  )
}

/**
 * True si un document mission (contrat, addendum, contrat client) a déjà une signature.
 * Le contrat cadre est exclu : il ne contient pas les infos mission, donc sa signature
 * ne doit pas bloquer l'update tant que le freelance n'a pas accepté.
 */
export function missionHasCollectedDocumentSignatures(mission) {
  if (!mission) return false

  const docs = mission.documents
  const groups = []
  if (Array.isArray(docs)) {
    groups.push(docs)
  } else if (docs && typeof docs === 'object') {
    groups.push(
      docs.normal_documents,
      docs.extension_documents,
      docs.other_documents,
      docs.normalDocuments,
      docs.extensionDocuments,
      docs.otherDocuments
    )
  }

  const hasNonMasterAgreementSignature = (group) =>
    Array.isArray(group) &&
    group.some((doc) => !isMasterAgreementDocument(doc) && documentHasCollectedSignature(doc))

  if (groups.some(hasNonMasterAgreementSignature)) {
    return true
  }

  const addenda = mission.addenda
  return Array.isArray(addenda) && addenda.some(documentHasCollectedSignature)
}

/**
 * Modifiable tant qu’aucune signature contractuelle mission n’a été collectée
 * (acceptation freelance ou approbation membre en attente).
 * Les signatures du contrat cadre sont ignorées : le MA ne porte pas les infos mission.
 */
export function canUpdateMissionBeforeFreelancerAcceptance(
  mission,
  { hasPendingAddendaForEnterprise = false } = {}
) {
  if (!mission || hasPendingAddendaForEnterprise) return false
  if (missionHasCollectedDocumentSignatures(mission)) return false

  const normalizedStatus = normalizeMissionLifecycleStatus(mission.status)

  if (normalizedStatus === MISSION_LIFECYCLE_STATUS.AWAITING_FREELANCER_ACCEPTANCE) {
    return true
  }

  if (normalizedStatus === MISSION_LIFECYCLE_STATUS.DRAFT && isMissionPendingMemberApproval(mission)) {
    return true
  }

  return false
}

/** Plafond CRA : verrouillé une fois la mission au moins « en cours » (contrat signé). */
export function isMissionMaxBillableCapLockedStatus(status) {
  if (status == null || status === '') return false
  const n = String(status).trim().toUpperCase().replace(/-/g, '_')
  return (
    n === MISSION_LIFECYCLE_STATUS.IN_PROGRESS ||
    n === MISSION_LIFECYCLE_STATUS.COMPLETED ||
    n === MISSION_LIFECYCLE_STATUS.TERMINATED
  )
}

export const MISSION_STATUS_TO_DISPLAY = {
  [MISSION_LIFECYCLE_STATUS.DRAFT]: 'draft',
  [MISSION_LIFECYCLE_STATUS.AWAITING_FREELANCER_ACCEPTANCE]: 'awaiting-freelancer-acceptance',
  [MISSION_LIFECYCLE_STATUS.AWAITING_TEAM_SIGNATURE]: 'awaiting-company-signature',
  [MISSION_LIFECYCLE_STATUS.IN_PROGRESS]: 'in-progress',
  [MISSION_LIFECYCLE_STATUS.COMPLETED]: 'completed',
  [MISSION_LIFECYCLE_STATUS.TERMINATED]: 'terminated',
  [MISSION_LIFECYCLE_STATUS.DELETED]: 'deleted',
  [MISSION_LIFECYCLE_STATUS.REJECTED]: 'rejected',
  [MISSION_LIFECYCLE_STATUS.CANCELLED]: 'cancelled',
};

// Dynamic statuses based on dates (for in-progress missions)
export const DYNAMIC_STATUS = {
  UPCOMING: 'upcoming',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
};

export const DEFAULT_DISPLAY_STATUS = 'awaiting-freelancer-acceptance';

/** Missions where the freelancer must sign, accept, or sign an addendum (badges, notifications). */
export function isFreelancerMissionActionDisplayStatus(status) {
  if (!status) return false
  return (
    status === 'pending-freelancer-review' ||
    status === 'pending-freelancer-signature' ||
    status === 'pending-freelancer-extension'
  )
}

/**
 * Get the display status for a mission
 * 
 * Base value comes from the backend (packages/nextjs/lib/utils/missionDisplayStatus.ts).
 * Exception: en session freelance, pending-freelancer-review est mappé vers pending-freelancer-signature
 * pour les libellés (même intention « action à faire » côté freelance).
 * 
 * @param {Object} mission - Mission object from API
 * @param {string} mission.display_status - Display status calculated by backend (REQUIRED)
 * @param {boolean} isFreelancer - Whether the current user is a freelancer (badges / libellés « action à faire »)
 * @returns {string} - The display status to show
 */
export const getMissionDisplayStatus = (mission, isFreelancer) => {
  if (!mission) {
    console.warn('[getMissionDisplayStatus] Mission is null or undefined')
    return DEFAULT_DISPLAY_STATUS
  }
  
  // Support both snake_case (from API) and camelCase (from normalized objects)
  const displayStatus = mission.display_status || mission.displayStatus
  
  if (displayStatus) {
    // Le backend renvoie pending-freelancer-review quand l'entreprise attend le freelance.
    // Si la session est freelance, on affiche le même libellé court que pour une action à signer.
    if (
      isFreelancer &&
      displayStatus === 'pending-freelancer-review' &&
      !isClientContractMission(mission)
    ) {
      return 'pending-freelancer-signature'
    }
    return displayStatus
  }
  
  // If backend didn't provide display_status, log error and return pending-freelancer-review
  // This should not happen in normal flow as backend always calculates it
  // Use pending-freelancer-review instead of awaiting-freelancer-acceptance as it's more accurate
  console.error(
    '[getMissionDisplayStatus] CRITICAL: Backend did not provide display_status for mission:',
    mission.id || 'unknown',
    'This is a backend bug - display_status should always be calculated server-side.'
  )
  
  // Use pending-freelancer-review as fallback instead of awaiting-freelancer-acceptance
  // This indicates that action is needed, which is more accurate than awaiting-freelancer-acceptance
  return 'pending-freelancer-review'
}

const PORTAGE_STATUS_LABEL_OVERRIDES = {
  'pending-freelancer-review': 'awaiting-umbrella-review',
  'pending-freelancer-signature': 'awaiting-umbrella-signature',
  'pending-freelancer-extension': 'awaiting-umbrella-extension',
  'awaiting-freelancer-acceptance': 'awaiting-umbrella-acceptance',
};

const CLIENT_CONTRACT_STATUS_LABEL_OVERRIDES = {
  'pending-client-signature': 'awaiting-client-signature',
  'awaiting-client-signature': 'awaiting-client-signature',
};

function isClientContractMission(mission) {
  if (!mission) return false;
  return Boolean(mission.has_client_contract ?? mission.hasClientContract);
}

function getMissionFreelancerLegalEntityType(mission) {
  if (!mission) return null;
  return (
    mission.freelancer_legal_entity_type ??
    mission.freelancerLegalEntityType ??
    mission.assignee?.legalEntityType ??
    mission.assignee?.legal_entity_type ??
    null
  );
}

function isPortageMission(mission) {
  return isPortage(getMissionFreelancerLegalEntityType(mission));
}

/**
 * Libellé i18n du badge statut (interpolation date pour résiliations programmées / effectuées).
 * @param {{ shortDate?: boolean }} [options] - shortDate: DD/MM/YY (liste) ; sinon DD/MM/YYYY (fiche mission).
 */
export function getMissionStatusLabel(t, displayStatus, mission, options = {}) {
  if (!displayStatus) return '';

  const { shortDate = false } = options;
  const dateFormat = shortDate ? 'DD/MM/YY' : 'DD/MM/YYYY';

  const effectiveStatus =
    isClientContractMission(mission) && CLIENT_CONTRACT_STATUS_LABEL_OVERRIDES[displayStatus]
      ? CLIENT_CONTRACT_STATUS_LABEL_OVERRIDES[displayStatus]
      : isPortageMission(mission) && PORTAGE_STATUS_LABEL_OVERRIDES[displayStatus]
      ? PORTAGE_STATUS_LABEL_OVERRIDES[displayStatus]
      : displayStatus;

  if (
    effectiveStatus === 'scheduled-early-termination' ||
    effectiveStatus === 'early-terminated'
  ) {
    const effectiveDate =
      mission?.terminationEffectiveDate ??
      mission?.termination_effective_date ??
      mission?.endDate ??
      mission?.end_date;
    const formattedDate = effectiveDate ? dayjs(effectiveDate).format(dateFormat) : '';
    if (formattedDate) {
      return t(`missions.status.${effectiveStatus}`, { date: formattedDate });
    }
    if (effectiveStatus === 'early-terminated') {
      return t('missions.status.terminated');
    }
    return t('missions.status.scheduled-early-termination', { date: '' });
  }

  return t(`missions.status.${effectiveStatus}`);
}

/**
 * Get status badge props (className) for a given mission status
 * Centralized function to ensure consistent status styling across the app
 * @param {string} status - The status value (can be in various formats: kebab-case, snake_case, etc.)
 * @param {Object} options - Optional configuration
 * @param {boolean} options.includeHover - Whether to include hover styles (default: false)
 * @param {boolean} options.includeBorder - Border ~20% darker than badge background (default: false)
 * @returns {Object} - Object with className property for the badge
 */
export const getMissionStatusBadgeProps = (status, options = {}) => {
  const { includeHover = false, includeBorder = false } = options;

  const buildClassName = (config) => {
    const parts = [config.base];
    if (includeBorder) {
      const bgClass = config.base.split(/\s+/).find((c) => c.startsWith('bg-'));
      const borderClass = bgClass ? borderClassFromBgClass(bgClass) : null;
      if (borderClass) parts.push(borderClass);
    }
    if (includeHover && config.hover) parts.push(config.hover);
    return parts.join(' ');
  };
  
  const baseStatusMap = {
    // New unified statuses
    'pending-freelancer-review': { base: 'bg-yellow-100 text-yellow-700', hover: 'hover:bg-yellow-100' },
    'pending-freelancer-signature': { base: 'bg-yellow-100 text-yellow-700', hover: 'hover:bg-yellow-100' },
    'pending-freelancer-extension': { base: 'bg-yellow-100 text-yellow-700', hover: 'hover:bg-yellow-100' },
    'pending-company-review': { base: 'bg-orange-100 text-orange-700', hover: 'hover:bg-orange-100' },
    'pending-client-signature': { base: 'bg-yellow-100 text-yellow-700', hover: 'hover:bg-yellow-100' },
    'awaiting-client-signature': { base: 'bg-yellow-100 text-yellow-700', hover: 'hover:bg-yellow-100' },
    'in-progress': { base: 'bg-blue-100 text-blue-700', hover: 'hover:bg-blue-100' },
    'early-terminated': { base: 'bg-red-100 text-red-700', hover: 'hover:bg-red-100' },
    'scheduled-early-termination': { base: 'bg-orange-100 text-orange-700', hover: 'hover:bg-orange-100' },
    'completed': { base: 'bg-green-100 text-green-700', hover: 'hover:bg-green-100' },
    // Current kebab-case statuses (for backward compatibility)
    'draft': { base: 'bg-gray-100 text-gray-700', hover: 'hover:bg-gray-100' },
    'awaiting-freelancer-acceptance': { base: 'bg-yellow-100 text-yellow-700', hover: 'hover:bg-yellow-100' },
    'awaiting-company-signature': { base: 'bg-orange-100 text-orange-700', hover: 'hover:bg-orange-100' },
    'upcoming': { base: 'bg-purple-100 text-purple-700', hover: 'hover:bg-purple-100' },
    'terminated': { base: 'bg-red-100 text-red-700', hover: 'hover:bg-red-100' },
    'deleted': { base: 'bg-gray-200 text-gray-600', hover: 'hover:bg-gray-200' },
    'rejected': { base: 'bg-red-100 text-red-700', hover: 'hover:bg-red-100' },
    'cancelled': { base: 'bg-gray-200 text-gray-600', hover: 'hover:bg-gray-200' },
    // Legacy support for old status formats
    'awaiting_signature': { base: 'bg-yellow-100 text-yellow-700', hover: 'hover:bg-yellow-100' },
    'pending-signature': { base: 'bg-yellow-100 text-yellow-700', hover: 'hover:bg-yellow-100' },
    'pending-approval': { base: 'bg-sky-100 text-sky-800', hover: 'hover:bg-sky-100' },
    'created': { base: 'bg-green-100 text-green-700', hover: 'hover:bg-green-100' },
  };

  if (!status) {
    const defaultConfig = {
      base: 'bg-yellow-100 text-yellow-700',
      hover: 'hover:bg-yellow-100',
    };
    return { className: buildClassName(defaultConfig) };
  }

  // Normalize status: convert to lowercase and replace underscores with hyphens
  const normalizedStatus = status.toLowerCase().replace(/_/g, '-');
  
  // Find matching status (try normalized first, then original)
  const statusConfig = baseStatusMap[normalizedStatus] || baseStatusMap[status];
  
  if (!statusConfig) {
    const defaultConfig = {
      base: 'bg-yellow-100 text-yellow-700',
      hover: 'hover:bg-yellow-100',
    };
    return { className: buildClassName(defaultConfig) };
  }

  return { className: buildClassName(statusConfig) };
};
